<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { useVuelidate } from '@vuelidate/core'
import { required } from '@vuelidate/validators'
import { useUmsokn } from '@/composables/Applications/UseUmsokn'

const props = defineProps<{ disable?: boolean }>()

const { t } = useI18n()
const { umsokn, onLoaded } = useUmsokn()

const rules = {
  umsokn: {
    loanBefore: {
      $each: {
        movedProperty: { required },
        overProperty: { required },
        vedRetturLoan: { required },
        vedRettur: { required },
        initialHofudstoll: { required },
        creditor: { required }
      }
    }
  }
}

const v$ = useVuelidate(rules, { umsokn })

defineExpose({
  validate() {
    v$.value.$touch()

    const rows = (v$.value.umsokn.loanBefore as any)?.$each
    if (rows) {
      Object.values(rows).forEach((row: any) => row.$touch())
    }

    return !v$.value.$invalid
  }
})

onLoaded(() => {
  if (!umsokn.value) return

  if (!Array.isArray(umsokn.value.loanBefore) || umsokn.value.loanBefore.length === 0) {
    umsokn.value.loanBefore = [
      {
        movedProperty: '',
        overProperty: '',
        vedRetturLoan: '',
        vedRettur: '',
        initialHofudstoll: '',
        creditor: ''
      }
    ]
  }
})

function addLoanBefore() {
  umsokn.value?.loanBefore.push({
    movedProperty: '',
    overProperty: '',
    vedRetturLoan: '',
    vedRettur: '',
    initialHofudstoll: '',
    creditor: ''
  })
}

function removeLoanBefore(i: number) {
  umsokn.value?.loanBefore.splice(i, 1)
  if (umsokn.value && umsokn.value.loanBefore.length === 0) {
    addLoanBefore()
  }
}
</script>

<template>
  <div v-if="umsokn">
    <v-col>
      <div class="forms__header">
        {{ t('lan_vedflutning_step4_header') }}
      </div>

      <transition-group name="list">
        <div v-for="(item, i) in umsokn.loanBefore" :key="'loanBefore_' + i">
          <div v-if="i > 0" class="forms__divider__container">
            <div class="forms__divider"></div>
            <button
              v-if="!props.disable"
              class="forms__warning__list__wrapper"
              @click="removeLoanBefore(i)"
            >
              <img class="forms__warning__close" src="@/assets/icons/Close.svg" />
            </button>
          </div>

          <!-- Moved property -->
          <v-row>
            <v-col cols="12">
              <v-text-field
                v-model="item.movedProperty"
                :readonly="props.disable"
                :label="t('lan_vedflutning_step4_flutt_eign')"
                variant="outlined"
                :error="v$.umsokn.loanBefore[i]?.movedProperty?.$error"
                :error-messages="v$.umsokn.loanBefore[i]?.movedProperty?.$error
                  ? [t('lan_vedflutning_step4_flutt_eign_error')]
                  : []"
              />
            </v-col>
          </v-row>

          <!-- Over property -->
          <v-row>
            <v-col cols="12">
              <v-text-field
                v-model="item.overProperty"
                :readonly="props.disable"
                :label="t('lan_vedflutning_step4_flutt_yfir')"
                variant="outlined"
                :error="v$.umsokn.loanBefore[i]?.overProperty?.$error"
                :error-messages="v$.umsokn.loanBefore[i]?.overProperty?.$error
                  ? [t('lan_vedflutning_step4_flutt_eign_error')]
                  : []"
              />
            </v-col>
          </v-row>

          <!-- VedRettur loan -->
          <v-row>
            <v-col cols="12">
              <v-text-field
                v-model="item.vedRetturLoan"
                :readonly="props.disable"
                :label="t('lan_vedflutning_step4_vedrettur_lans')"
                variant="outlined"
                :error="v$.umsokn.loanBefore[i]?.vedRetturLoan?.$error"
              />
            </v-col>
          </v-row>

          <!-- Sub-section -->
          <v-row>
            <v-col cols="12">
              <p style="font-weight:bold">
                {{ t('lan_vedflutning_step4_ahvilandi') }}
              </p>
            </v-col>

            <v-col cols="12" md="4">
              <v-text-field
                v-model="item.vedRettur"
                :readonly="props.disable"
                :label="t('lan_vedflutning_step4_vedrettur')"
                variant="outlined"
                :error="v$.umsokn.loanBefore[i]?.vedRettur?.$error"
              />
            </v-col>

            <v-col cols="12" md="4">
              <v-text-field
                v-model="item.initialHofudstoll"
                :readonly="props.disable"
                :label="t('lan_vedflutning_step4_hofudstoll')"
                variant="outlined"
                :error="v$.umsokn.loanBefore[i]?.initialHofudstoll?.$error"
              />
            </v-col>

            <v-col cols="12" md="4">
              <v-text-field
                v-model="item.creditor"
                :readonly="props.disable"
                :label="t('lan_vedflutning_step4_krofuhafi')"
                variant="outlined"
                :error="v$.umsokn.loanBefore[i]?.creditor?.$error"
              />
            </v-col>
          </v-row>
        </div>
      </transition-group>

      <div class="m-t-40" v-if="!props.disable">
         <v-btn
          color="primary"
          variant="outlined"
          prepend-icon="mdi-plus"
          class="mt-4"
          @click="addLoanBefore"
        >
          {{ $t('lan_vedflutning_step4_add') }}
        </v-btn>
        
      </div>
    </v-col>
  </div>
</template>

<style scoped lang="scss">
.forms__divider__container {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  margin-bottom: 12px;
  position: relative;
}
.forms__divider {
  flex: 1;
  height: 1px;
  background-color: #ddd;
  margin-right: 8px;
}
.forms__warning__list__wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 50%;
  background-color: transparent;
  cursor: pointer;
  &:hover {
    background-color: #f5f5f5;
  }
}
.forms__warning__close {
  width: 16px;
  height: 16px;
  pointer-events: none;
}
</style>
